import { getSupabase } from '../../lib/supabaseClient';
import type { HeadedOutDataProvider, HeadedOutRow } from './types';

let provider: HeadedOutDataProvider | null = null;

export function getHeadedOutProvider(): HeadedOutDataProvider {
  if (provider) return provider;
  const sb = getSupabase();
  if (sb) {
    provider = new SupabaseHeadedOut();
  } else {
    provider = new MemoryHeadedOut();
  }
  return provider;
}

export function setHeadedOutProviderForTests(p: HeadedOutDataProvider | null) {
  provider = p;
}

class SupabaseHeadedOut implements HeadedOutDataProvider {
  private _lastFamilyMap: Record<string, string> = {};
  private _lastParkMap: Record<string, string> = {};

  async listActiveUpcoming(): Promise<HeadedOutRow[]> {
    const client = getSupabase();
    if (!client) throw new Error('backend_unconfigured');
    const { data, error } = await client
      .from('announcements_active_upcoming')
      .select('id, family_id, park_id, kind, planned_at, created_at');
    if (error) throw error;
    const rows = (data ?? []).map((r: any) => ({
      id: r.id as string,
      familyId: r.family_id as string,
      parkId: r.park_id as string,
      kind: r.kind as 'now' | 'later',
      plannedAt: r.planned_at ? new Date(r.planned_at as string) : null,
      createdAt: new Date(r.created_at as string),
    }));
    // Fetch names separately to avoid view embedding issues
    const familyIds = Array.from(new Set(rows.map((r) => r.familyId)));
    const parkIds = Array.from(new Set(rows.map((r) => r.parkId)));
    const [families, parks] = await Promise.all([
      familyIds.length > 0
        ? client.from('families').select('id, display_name').in('id', familyIds)
        : Promise.resolve({ data: [], error: null }),
      parkIds.length > 0
        ? client.from('parks').select('id, name').in('id', parkIds)
        : Promise.resolve({ data: [], error: null }),
    ]);
    // RLS-safe: families limited columns; parks readable in neighborhood
    const famMap: Record<string, string> = {};
    for (const f of (families.data ?? []) as any[]) {
      famMap[f.id as string] = f.display_name as string;
    }
    const parkMap: Record<string, string> = {};
    for (const p of (parks.data ?? []) as any[]) {
      parkMap[p.id as string] = p.name as string;
    }
    // Attach derived names directly onto rows via side-channels for UI mapping layer
    // UI layer will re-lookup using these maps passed in
    this._lastFamilyMap = famMap;
    this._lastParkMap = parkMap;
    return rows;
  }

  async endAnnouncement(id: string, reason: 'left' | 'cancelled'): Promise<void> {
    const client = getSupabase();
    if (!client) throw new Error('backend_unconfigured');
    const { error } = await client.rpc('end_announcement', { aid: id, reason } as any);
    if (error) throw error;
  }

  // Expose last maps to caller if needed
  getLastFamilyMap(): Record<string, string> {
    return this._lastFamilyMap;
  }
  getLastParkMap(): Record<string, string> {
    return this._lastParkMap;
  }
}

class MemoryHeadedOut implements HeadedOutDataProvider {
  private rows: HeadedOutRow[] = [];
  async listActiveUpcoming(): Promise<HeadedOutRow[]> {
    return this.rows.slice();
  }
  async endAnnouncement(id: string, _reason: 'left' | 'cancelled'): Promise<void> {
    this.rows = this.rows.filter((r) => r.id !== id);
  }
}

